import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient } from '@angular/common/http'
import { ApplicationConfig, enableProdMode, importProvidersFrom } from '@angular/core'
import { bootstrapApplication } from '@angular/platform-browser'
import { PreloadAllModules, provideRouter, withPreloading } from '@angular/router'
import { environment } from '@env/environment'
import { AngularCommonModule, PhraseProject } from '@gofive/angular-common'
import { TranslateLoader, TranslateModule } from '@ngx-translate/core'
import { SweetAlert2Module } from '@sweetalert2/ngx-sweetalert2'
import { AuthConfig, OAuthModuleConfig, OAuthStorage, ValidationHandler, provideOAuthClient } from 'angular-oauth2-oidc'
import { JwksValidationHandler } from 'angular-oauth2-oidc-jwks'
import { AuthGuardWithForcedLogin } from 'src/domain/guards/auth.guard'
import { authConfig } from 'src/domain/services/auth/auth-config'
import { authModuleConfig } from 'src/domain/services/auth/auth-module-config'
import { AuthService } from 'src/domain/services/auth/auth.service'
import { BaseService } from 'src/domain/services/base.service'
import { Phrasei18nService } from 'src/domain/services/phrasei18n/phrasei18n.service'
import { SettingsService } from 'src/domain/services/setting/settings.service'
import { ClientImplementationRepository } from './data/repositories/client-implementation.repository'
import { CommonImplementationRepository } from './data/repositories/common-implementation.repository'
import { ComponentsImplementationRepository } from './data/repositories/components-implementation.repository'
import { InvoiceImplementationRepository } from './data/repositories/invoice-implementation.repository'
import { MasterTypeImplementationRepository } from './data/repositories/mastertype-implementation.repository'
import { PlanImplementationRepository } from './data/repositories/plan-implementation.repository'
import { ProductImplementationRepository } from './data/repositories/product-implementation.repository'
import { ProductItemImplementationRepository } from './data/repositories/product-item-implementation.repository'
import { RegisterImplementationRepository } from './data/repositories/register-implementation.repository'
import { SubscriptionImplementationRepository } from './data/repositories/subscription-implementation.repository'
import { ClientRepository } from './domain/repositories/client.repository'
import { CommonRepository } from './domain/repositories/common.repository'
import { ComponentsRepository } from './domain/repositories/component.repository'
import { InvoiceRepository } from './domain/repositories/invoice.repository'
import { MasterTypeRepository } from './domain/repositories/mastertype.repository'
import { PlanRepository } from './domain/repositories/plan.repository'
import { ProductItemRepository } from './domain/repositories/product-item.repository'
import { ProductRepository } from './domain/repositories/product.repository'
import { RegisterRepository } from './domain/repositories/register.repository'
import { SubscriptionRepository } from './domain/repositories/subscription.repository'
import { TokenInterceptorService } from './domain/services/auth/token-interceptor.service'
import { AppComponent } from './presentation/app.component'
import { routes } from './presentation/app.routes'

if (environment.production) {
	enableProdMode()
}

function storageFactory(): OAuthStorage {
	return localStorage
}

function createTranslateLoader(httpClient: HttpClient) {
	return new Phrasei18nService(httpClient)
}

const provideTranslation = () => ({
	loader: {
		provide: TranslateLoader,
		useFactory: createTranslateLoader,
		deps: [HttpClient],
	},
	useDefaultLang: true,
	defaultLanguage: 'th-TH',
})

const appConfig: ApplicationConfig = {
	providers: [
		provideRouter(routes, withPreloading(PreloadAllModules)),
		provideOAuthClient(),
		provideHttpClient(),
		importProvidersFrom([TranslateModule.forRoot(provideTranslation())]),
		importProvidersFrom([
			AngularCommonModule.forRoot({
				apiUrlBase: environment.apiUrl,
				phraseProject: PhraseProject.ClientPortal,
				assetsEnvironment: environment.envName,
				tolgeeOptions: {
					language: 'en-GB',
					defaultLanguage: 'en-GB',
					apiUrl: 'https://tolgee.gofive.co.th/',
					apiKey: 'tgpak_gnpxc2jvnrsg4mlpobutozdog5zwqmdvnfygcytfgu4ww',
					ns: [PhraseProject.GofiveCore, PhraseProject.Venio, PhraseProject.empeo, PhraseProject.ClientPortal],
					defaultNs: PhraseProject.GofiveCore,
					fallbackNs: PhraseProject.GofiveCore,
					fallbackLanguage: 'en-GB',
				},
			}),
		]),
		importProvidersFrom([SweetAlert2Module.forRoot()]),
		{ provide: 'IS_URL', useValue: environment.issuer },
		{ provide: 'API_URL', useValue: environment.apiUrl },
		{ provide: 'EMPEO_URL', useValue: environment.empeoUrl },
		{ provide: 'ASSETS_URL', useValue: environment.assetUrl },
		{ provide: AuthConfig, useValue: authConfig },
		{ provide: OAuthModuleConfig, useValue: authModuleConfig },
		{ provide: ValidationHandler, useClass: JwksValidationHandler },
		{ provide: OAuthStorage, useFactory: storageFactory },
		{ provide: ClientRepository, useClass: ClientImplementationRepository },
		{ provide: ComponentsRepository, useClass: ComponentsImplementationRepository },
		{ provide: CommonRepository, useClass: CommonImplementationRepository },
		{ provide: MasterTypeRepository, useClass: MasterTypeImplementationRepository },
		{ provide: InvoiceRepository, useClass: InvoiceImplementationRepository },
		{ provide: PlanRepository, useClass: PlanImplementationRepository },
		{ provide: ProductItemRepository, useClass: ProductItemImplementationRepository },
		{ provide: ProductRepository, useClass: ProductImplementationRepository },
		{ provide: RegisterRepository, useClass: RegisterImplementationRepository },
		{ provide: SubscriptionRepository, useClass: SubscriptionImplementationRepository },
		{ provide: HTTP_INTERCEPTORS, useClass: TokenInterceptorService, multi: true },
		BaseService,
		AuthService,
		SettingsService,
		AuthGuardWithForcedLogin,
	],
}

bootstrapApplication(AppComponent, appConfig).catch((err) => console.error(err))
